import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeEvents } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { LazyMapView } from '../components/LazyMap.js';
import { StatusPill } from '../components/StatusPill.js';
import { liveSnapshot } from '../api/admin.js';
import { useSocket } from '../hooks/useSocket.js';

export function LiveMapPage() {
  const socket = useSocket();
  const queryClient = useQueryClient();
  const live = useQuery({ queryKey: ['live'], queryFn: liveSnapshot, refetchInterval: 5000 });

  useEffect(() => {
    if (!socket) return;
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['live'] });
    socket.on(RealtimeEvents.TripUpdate, invalidate);
    socket.on(RealtimeEvents.DriverLocation, invalidate);
    return () => {
      socket.off(RealtimeEvents.TripUpdate, invalidate);
      socket.off(RealtimeEvents.DriverLocation, invalidate);
    };
  }, [socket, queryClient]);

  const drivers = live.data?.drivers ?? [];
  const trips = live.data?.trips ?? [];

  return (
    <Shell title="Live map" subtitle="Driver positions and trips in flight.">
      <div className="grid h-[calc(100svh-9rem)] grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
        <LazyMapView drivers={drivers} trips={trips} />

        <aside className="card flex h-full min-h-0 flex-col">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="font-display text-base font-semibold">Active trips</p>
            <p className="text-xs text-slate-500">{trips.length} in flight</p>
          </div>
          <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
            {trips.length === 0 ? (
              <li className="px-4 py-6 text-sm text-slate-500">Nothing in flight right now.</li>
            ) : (
              trips.map((t) => (
                <li key={t.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <StatusPill status={t.status} />
                    <span className="text-xs text-slate-500">{t.driver ?? '—'}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-900">{t.rider}</p>
                  <p className="text-xs text-slate-500">
                    {t.pickup.label} → {t.drop.label}
                  </p>
                </li>
              ))
            )}
          </ul>
        </aside>
      </div>
    </Shell>
  );
}
